import type { ColumnDef } from '@tanstack/react-table';

import { DataTableRowActions } from '@components/DataTable/DataTableRowActions';
import { UserCell } from '@components/UserCell';
import { EditIcon, TrashIcon } from '@components/icons';
import type { CalculationType } from '@api/calculationType';
import { PRICE_MODIFIER_COLUMN_WIDTHS } from '../DataTable/columnWidths';

import { formatAmount } from './format';

export interface PriceModifierOwner {
  id: number;
  name: string;
  email: string;
  avatar_url: string | null;
}

export interface PriceModifierRow {
  id: number;
  name: string;
  description: string | null;
  amount: string;
  calculation_type: CalculationType;
  user: PriceModifierOwner | null;
}

export interface PriceModifierColumnCallbacks {
  onEdit: (row: PriceModifierRow) => void;
  onDelete: (row: PriceModifierRow) => void;
}

export interface PriceModifierColumnOptions {
  // RBAC: the row-action column is included only for writers, and its actions
  // (Edit, Delete) render per-row only on records the current user owns, unless
  // canManageAll is set (admin/sales admin manage every record). Omitted
  // entirely for non-writers.
  canWrite?: boolean;
  currentUserId?: number;
  canManageAll?: boolean;
}

export function createPriceModifierColumns(
  callbacks: PriceModifierColumnCallbacks,
  options: PriceModifierColumnOptions = {},
): ColumnDef<PriceModifierRow>[] {
  const { canWrite = false, currentUserId, canManageAll = false } = options;
  return [
    {
      accessorKey: 'name',
      header: 'Name',
      enableSorting: false,
      cell: ({ row }) => {
        const description = row.original.description?.trim();
        return (
          <div className="flex min-h-13 flex-col justify-center gap-1">
            <span className="text-sm font-semibold leading-[1.5] text-off-black">
              {row.original.name}
            </span>
            {description && (
              <span className="text-xs leading-[1.25] text-grey line-clamp-1">
                {description}
              </span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'amount',
      header: 'Amount',
      size: PRICE_MODIFIER_COLUMN_WIDTHS.amount,
      enableSorting: false,
      cell: ({ row }) => (
        <span className="font-inter text-sm text-off-black whitespace-nowrap">
          {formatAmount(row.original.amount, row.original.calculation_type)}
        </span>
      ),
    },
    {
      accessorKey: 'user',
      header: 'Owner',
      size: PRICE_MODIFIER_COLUMN_WIDTHS.owner,
      enableSorting: false,
      cell: ({ row }) => <UserCell user={row.original.user} truncateAfter={14} />,
    },
    ...(canWrite
      ? [
          {
            id: 'actions',
            header: () => null,
            enableSorting: false,
            cell: ({ row }) => {
              // Owner-scoped, unless the current user can manage all records
              // (admin/sales admin) — only then does a non-owner see the
              // Edit/Delete actions.
              const owned =
                canManageAll ||
                (row.original.user?.id != null && row.original.user.id === currentUserId);
              if (!owned) return null;
              return (
                <span onClick={(e) => e.stopPropagation()}>
                  <DataTableRowActions
                    actions={[
                      {
                        label: 'Edit',
                        icon: <EditIcon />,
                        onClick: () => callbacks.onEdit(row.original),
                      },
                      {
                        label: 'Delete',
                        icon: <TrashIcon />,
                        onClick: () => callbacks.onDelete(row.original),
                        destructive: true,
                      },
                    ]}
                  />
                </span>
              );
            },
          } as ColumnDef<PriceModifierRow>,
        ]
      : []),
  ];
}
